import { useValue } from "@legendapp/state/react";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
} from "@repo/ui/combobox";
import { Icon } from "@repo/ui/components/icon";
import { Button } from "@repo/ui/ui/button";
import { Field, FieldGroup, FieldLabel } from "@repo/ui/ui/field";
import { Spinner } from "@repo/ui/ui/spinner";
import { useStore } from "@tanstack/react-form";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { getRouter } from "~/router";
import {
  createDatasource,
  testDatasource,
} from "~/stores/datasources/datasources.actions";
import { activeOrganization$ } from "~/stores/organizations";
import { planLimitReasonFromError } from "~/stores/subscription";
import { BigQueryFields } from "./bigquery-fields";
import { ClickHouseFields } from "./clickhouse-fields";
import { DatabricksFields } from "./databricks-fields";
import {
  buildCreatePayload,
  DATASOURCE_TYPE_OPTIONS,
  type DatasourceForm,
  type DatasourceTypeKey,
  type DatasourceTypeOption,
  deriveDatasourceName,
  useDatasourceForm,
} from "./datasource-new.types";
import { SnowflakeFields } from "./snowflake-fields";
import { SqlFields } from "./sql-fields";

const FORM_ID = "datasource-form";

type ConnectionStatus = "idle" | "testing";

/**
 * Watches the credential fields that influence the derived datasource name
 * and auto-fills `name` only when it's still empty. If the user has typed
 * anything into the name field — manually or via a prior auto-fill — we
 * leave it alone. Renders nothing; exists purely for its side effect.
 */
const AutoNameSync = ({ form }: { form: DatasourceForm }) => {
  const slice = useStore(form.store, (s) => ({
    type: s.values.type,
    database: s.values.database,
    catalog: s.values.catalog,
    chDatabase: s.values.chDatabase,
    url: s.values.url,
    serviceAccount: s.values.serviceAccount,
    name: s.values.name,
  }));

  useEffect(() => {
    if (slice.name !== "") return;
    const derived = deriveDatasourceName(
      form.state.values,
      slice.type as DatasourceTypeKey | "",
    );
    if (!derived) return;
    form.setFieldValue("name", derived);
  }, [
    slice.type,
    slice.database,
    slice.catalog,
    slice.chDatabase,
    slice.url,
    slice.serviceAccount,
    slice.name,
    form,
  ]);

  return null;
};

/** Renders the credential fields for the selected datasource type. */
const DatasourceFields = ({
  type,
  form,
}: {
  type: DatasourceTypeKey;
  form: ReturnType<typeof useDatasourceForm>;
}) => {
  switch (type) {
    case "POSTGRESQL":
      return (
        <SqlFields form={form} defaultPort="5432" urlScheme="postgresql" />
      );
    case "MYSQL":
      return <SqlFields form={form} defaultPort="3306" urlScheme="mysql" />;
    case "REDSHIFT":
      return <SqlFields form={form} defaultPort="5439" urlScheme="redshift" />;
    case "BIGQUERY":
      return <BigQueryFields form={form} />;
    case "CLICKHOUSE":
      return <ClickHouseFields form={form} />;
    case "SNOWFLAKE":
      return <SnowflakeFields form={form} />;
    case "DATABRICKS":
      return <DatabricksFields form={form} />;
  }
};

interface DatasourceNewFormProps {
  /**
   * Called after the datasource has been successfully created. Receives
   * the new datasource id so callers can route to id-bearing follow-up
   * screens (e.g. the onboarding catalog step) without an extra fetch.
   */
  onCreated: (datasourceId: string) => void | Promise<void>;
  /** Label of the primary submit button. Defaults to "Add datasource". */
  submitLabel?: string;
  /** Slot rendered on the right side of the action row, before the submit button. */
  secondaryAction?: ReactNode;
}

/**
 * Reusable card containing the create-datasource form: type/name fields,
 * the dynamic per-driver credential fields, a "Test connection" action,
 * and a submit button. Consumers supply the post-create callback and an
 * optional secondary action (Cancel link, Skip button, etc.).
 */
export const DatasourceNewForm = ({
  onCreated,
  submitLabel = "Add datasource",
  secondaryAction,
}: DatasourceNewFormProps) => {
  const [selectedType, setSelectedType] = useState<DatasourceTypeOption | null>(
    null,
  );
  const [inputValue, setInputValue] = useState("");
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>("idle");
  const orgName = useValue(activeOrganization$.name);
  const namePlaceholder = `${orgName ?? "Mora"} DB`;

  const form = useDatasourceForm(async (value) => {
    const payload = buildCreatePayload(value);
    if (!payload) return;

    try {
      // Spread rather than re-assembling field by field: the API validates
      // `type` and `credentials` as a discriminated union, which only holds
      // together if the payload stays one object.
      const datasourceId = await createDatasource({
        name: value.name,
        ...payload,
      });

      await onCreated(datasourceId);
    } catch (err) {
      // Plan limit hit — send the user to the Plans page instead of a raw
      // error toast.
      if (planLimitReasonFromError(err)) {
        getRouter().navigate({ to: "/settings/plans" });
        throw err;
      }
      toast.error("Couldn't add datasource", {
        description:
          err instanceof Error
            ? err.message
            : "Please check your credentials and try again.",
      });
      throw err;
    }
  });

  const handleTestConnection = async () => {
    const values = form.state.values;
    const payload = buildCreatePayload(values);
    if (!payload) return;

    setConnectionStatus("testing");

    try {
      await testDatasource({
        type: payload.type,
        credentials: payload.credentials,
        options: payload.options,
      } as any);

      setConnectionStatus("idle");
      toast.success("Connection successful", {
        description: (
          <span className="text-muted-foreground text-xs">
            Your credentials are valid.
          </span>
        ),
      });
    } catch {
      setConnectionStatus("idle");
      toast.error("Connection failed", {
        description: "Please check your credentials and try again.",
      });
    }
  };

  const isTypeSelected = selectedType !== null;

  return (
    <div className="flex flex-1 flex-col">
      <AutoNameSync form={form} />
      <form
        id={FORM_ID}
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <form.Field name="type">
              {(field) => (
                <Field>
                  <FieldLabel>Datasource type</FieldLabel>
                  <Combobox
                    value={selectedType}
                    onValueChange={(option) => {
                      setSelectedType(option);
                      field.handleChange(
                        option?.value ?? ("" as DatasourceTypeKey),
                      );
                    }}
                    items={DATASOURCE_TYPE_OPTIONS}
                    isItemEqualToValue={(a, b) => a?.value === b?.value}
                    inputValue={inputValue}
                    onInputValueChange={setInputValue}
                    onOpenChange={(open) => {
                      if (open) setInputValue("");
                    }}
                  >
                    <ComboboxTrigger
                      render={
                        <Button
                          variant="outline"
                          className="w-full justify-start font-normal data-placeholder:text-muted-foreground"
                        >
                          {selectedType && (
                            <Icon.Logo
                              name={selectedType.logo}
                              className="size-4"
                            />
                          )}
                          <ComboboxValue placeholder="Select type" />
                          <Icon name="chevron-down" className="ml-auto" />
                        </Button>
                      }
                    />
                    <ComboboxContent className="min-w-(--anchor-width)">
                      <ComboboxInput
                        showTrigger={false}
                        placeholder="Search datasources..."
                      />
                      <ComboboxSeparator />
                      <ComboboxEmpty>No datasources found</ComboboxEmpty>
                      <ComboboxList className="pt-0">
                        {(option: DatasourceTypeOption) => (
                          <ComboboxItem
                            key={option.value}
                            value={option}
                            className="items-center py-2"
                          >
                            <Icon.Logo
                              name={option.logo}
                              className="size-5 shrink-0"
                            />
                            <div className="flex flex-col gap-0.5 pr-5">
                              <span className="font-medium">
                                {option.label}
                              </span>
                              <span className="hidden text-[11px]/tight text-muted-foreground sm:block">
                                {option.description}
                              </span>
                            </div>
                          </ComboboxItem>
                        )}
                      </ComboboxList>
                    </ComboboxContent>
                  </Combobox>
                </Field>
              )}
            </form.Field>

            <form.AppField name="name">
              {(field) => (
                <field.TextField
                  label="Datasource name"
                  placeholder={namePlaceholder}
                  description="Name your datasource in Mora."
                  disabled={!isTypeSelected}
                />
              )}
            </form.AppField>
          </div>
        </FieldGroup>

        {isTypeSelected && (
          <DatasourceFields type={selectedType.value} form={form} />
        )}
      </form>

      <div className="mt-auto flex items-center justify-between pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={handleTestConnection}
          disabled={!isTypeSelected || connectionStatus === "testing"}
          className="w-28"
        >
          {connectionStatus === "testing" ? (
            <Spinner className="size-4" />
          ) : (
            "Test connection"
          )}
        </Button>

        <div className="flex items-center gap-2">
          {secondaryAction}
          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(isSubmitting) => (
              <Button
                type="submit"
                form={FORM_ID}
                disabled={!isTypeSelected || isSubmitting}
              >
                {isSubmitting ? <Spinner className="size-4" /> : submitLabel}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </div>
    </div>
  );
};
